import { assetsAxios, stockPricesAxios } from './client'
import { ENDPOINTS } from './endpoints'
import type { ApiResponse, PaginatedApiResponse, Asset, AssetGroup, Transaction, StockPrice, StockPriceHistory, AssetDailySnapshot, CryptoPrice, FxRate } from './types'

export const assetsAPI = {
  assets: {
    list: async (params?: Record<string, unknown>): Promise<ApiResponse<Asset[]>> => {
      const res = await assetsAxios.get(ENDPOINTS.assets, { params })
      return res.data
    },
    create: async (data: Record<string, unknown>): Promise<ApiResponse<Asset>> => {
      const res = await assetsAxios.post(ENDPOINTS.assets, data)
      return res.data
    },
    get: async (id: number): Promise<ApiResponse<Asset>> => {
      const res = await assetsAxios.get(ENDPOINTS.asset(id))
      return res.data
    },
    update: async (id: number, data: Record<string, unknown>): Promise<ApiResponse<Asset>> => {
      const res = await assetsAxios.patch(ENDPOINTS.asset(id), data)
      return res.data
    },
    delete: async (id: number): Promise<void> => {
      await assetsAxios.delete(ENDPOINTS.asset(id))
    },
    getTransactions: async (id: number, params?: Record<string, unknown>): Promise<PaginatedApiResponse<Transaction>> => {
      const res = await assetsAxios.get(ENDPOINTS.assetTransactions(id), { params })
      return res.data
    },
    recalculate: async (id: number): Promise<ApiResponse<Asset>> => {
      const res = await assetsAxios.post(ENDPOINTS.assetRecalculate(id))
      return res.data
    },
    recalculateAll: async (): Promise<ApiResponse<{ recalculated: number }>> => {
      const res = await assetsAxios.post(ENDPOINTS.assetsRecalculateAll)
      return res.data
    },
  },

  assetGroups: {
    list: async (params?: Record<string, unknown>): Promise<ApiResponse<AssetGroup[]>> => {
      const res = await assetsAxios.get(ENDPOINTS.assetGroups, { params })
      return res.data
    },
    create: async (data: Record<string, unknown>): Promise<ApiResponse<AssetGroup>> => {
      const res = await assetsAxios.post(ENDPOINTS.assetGroups, data)
      return res.data
    },
    update: async (id: number, data: Record<string, unknown>): Promise<ApiResponse<AssetGroup>> => {
      const res = await assetsAxios.patch(ENDPOINTS.assetGroup(id), data)
      return res.data
    },
    delete: async (id: number): Promise<void> => {
      await assetsAxios.delete(ENDPOINTS.assetGroup(id))
    },
  },

  history: {
    portfolio: async (id: number, from: string, to: string): Promise<ApiResponse<AssetDailySnapshot[]>> => {
      const res = await assetsAxios.get(ENDPOINTS.portfolioHistory(id), { params: { from, to } })
      return res.data
    },
    asset: async (id: number, from: string, to: string): Promise<ApiResponse<AssetDailySnapshot[]>> => {
      const res = await assetsAxios.get(ENDPOINTS.assetHistory(id), { params: { from, to } })
      return res.data
    },
    assetGroup: async (id: number, from: string, to: string): Promise<ApiResponse<AssetDailySnapshot[]>> => {
      const res = await assetsAxios.get(ENDPOINTS.assetGroupHistory(id), { params: { from, to } })
      return res.data
    },
  },
}

// ─── Stock Price API ─────────────────────────────────────────────────

export const stockPriceAPI = {
  getPrices: async (symbols: string[]): Promise<StockPrice[]> => {
    if (symbols.length === 0) return []
    const res = await stockPricesAxios.get(ENDPOINTS.stockPrices, {
      params: { symbols: symbols.join(',') },
    })
    return res.data?.data ?? []
  },
  getHistory: async (symbols: string[], from: string, to: string): Promise<StockPriceHistory[]> => {
    if (symbols.length === 0) return []
    const res = await stockPricesAxios.get(ENDPOINTS.stockPriceHistory, {
      params: { symbols: symbols.join(','), from, to, size: 1000 },
    })
    return res.data?.data ?? []
  },
  getCryptoPrices: async (symbols: string[]): Promise<CryptoPrice[]> => {
    if (symbols.length === 0) return []
    const res = await stockPricesAxios.get(ENDPOINTS.cryptoPrices, {
      params: { symbols: symbols.join(',') },
    })
    return res.data?.data ?? []
  },
  getFxRates: async (pairs?: string[]): Promise<FxRate[]> => {
    const params = pairs && pairs.length > 0 ? { pairs: pairs.join(',') } : undefined
    const res = await stockPricesAxios.get(ENDPOINTS.fxRates, { params })
    return res.data?.data ?? []
  },
}
